import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, buttonVariants, EmptyState } from '@eo/shared';

import { fetchExcessExportLink } from './api/api';
import type { DownloadStatus } from './types';

const ASK_AGAIN = 'Ask for it again from the client’s bill in Capacity.';

const COPY: Record<DownloadStatus, { title: string; description: string }> = {
  checking: { title: 'Opening your report…', description: 'This takes a moment.' },
  downloading: {
    title: 'Your report is downloading.',
    description: 'Check your browser’s downloads. You can close this page.',
  },
  preparing: {
    title: 'Your report is still being prepared.',
    description: 'Try again in a minute.',
  },
  failed: { title: 'This report could not be made.', description: ASK_AGAIN },
  expired: { title: 'This report expired.', description: ASK_AGAIN },
  'not-found': { title: 'This report does not exist.', description: ASK_AGAIN },
  forbidden: {
    title: 'You cannot open this report.',
    description: 'Only the client’s CFO or an admin can download its excess hours bill.',
  },
  error: { title: 'The report could not be opened.', description: 'Try again.' },
};

const RETRYABLE: ReadonlySet<DownloadStatus> = new Set(['preparing', 'error']);
const OFFERS_CAPACITY: ReadonlySet<DownloadStatus> = new Set([
  'preparing',
  'failed',
  'expired',
  'not-found',
  'error',
]);

const statusOf = (err: unknown): DownloadStatus => {
  const code = (err as { response?: { status?: number } }).response?.status;
  if (code === 404) return 'not-found';
  if (code === 403) return 'forbidden';
  return 'error';
};

// Where the emailed link lands: the platform's short-lived link is opened once
// on arrival, so the emailed link does the whole job in one click.
export default function ExcessExportDownloadPage() {
  const { id = '' } = useParams();
  const [status, setStatus] = useState<DownloadStatus>('checking');
  const started = useRef(false);

  const open = useCallback(async () => {
    setStatus('checking');
    try {
      const link = await fetchExcessExportLink(id);
      if (link.status === 'ready' && link.url) {
        const anchor = document.createElement('a');
        anchor.href = link.url;
        anchor.click();
        setStatus('downloading');
      } else {
        setStatus(link.status === 'ready' ? 'expired' : link.status);
      }
    } catch (err) {
      setStatus(statusOf(err));
    }
  }, [id]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void open();
  }, [open]);

  const copy = COPY[status];
  return (
    <section data-testid="excess-export-download" role="status">
      <EmptyState
        title={copy.title}
        description={copy.description}
        action={
          OFFERS_CAPACITY.has(status) ? (
            <div className="flex gap-2">
              {RETRYABLE.has(status) && <Button onClick={() => void open()}>Try again</Button>}
              <Link to="/capacity" className={buttonVariants({ variant: 'secondary' })}>
                Open Capacity
              </Link>
            </div>
          ) : undefined
        }
      />
    </section>
  );
}
